import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DeadlineBadge } from '@/components/ui/DeadlineBadge';
import type { MyBetsQuery } from '@/gql/graphql';

type BetItem = NonNullable<NonNullable<MyBetsQuery['myBets']>['items']>[number];

const PREVIEW_COUNT = 3;

interface OpenBetCardProps {
    bet: BetItem;
}

export function OpenBetCard({ bet }: OpenBetCardProps) {
    const { t } = useTranslation('bets');

    const race = bet.race;
    const rankings = useMemo(() => bet.rankings ?? [], [bet.rankings]);
    const genders = useMemo(() => [...new Set(rankings.map(r => r.gender).filter(Boolean))] as string[], [rankings]);

    return (
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
                <div className="flex-1 min-w-0">
                    <Link
                        to={`/races/${race?.id}`}
                        className="font-bold text-slate-900 hover:text-emerald-700 transition-colors truncate block"
                    >
                        {race?.name}
                    </Link>
                    <span className="mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700">
                        {t('list.pending')}
                    </span>
                </div>
            </div>

            {/* Gender sections */}
            <div className="space-y-4">
                {genders.map((gender) => (
                    <GenderSection key={gender} gender={gender} rankings={rankings.filter(r => r.gender === gender)} />
                ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 pt-3 mt-4 border-t border-slate-100">
                <div className="flex items-center gap-3 text-xs text-slate-400">
                    {race?.bettingDeadline && (
                        <DeadlineBadge deadline={race.bettingDeadline} className="text-xs" />
                    )}
                </div>

                <Link
                    to={`/races/${race?.id}/bet`}
                    className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
                >
                    <Pencil size={12} />
                    {t('list.edit')}
                </Link>
            </div>
        </div>
    );
}

type RankingItem = NonNullable<BetItem['rankings']>[number];

function GenderSection({ gender, rankings }: { gender: string; rankings: RankingItem[] }) {
    const { t } = useTranslation('bets');
    const [expanded, setExpanded] = useState(false);

    const genderLabel = gender === 'MALE' ? t('gender.MALE') : t('gender.FEMALE');

    const ranked = [...rankings]
        .filter(r => (r.position || 0) > 0)
        .sort((a, b) => (a.position || 0) - (b.position || 0));

    const dnfs = rankings.filter(r => r.position === 0);
    const allRunners = [...ranked, ...dnfs];
    const hasMore = allRunners.length > PREVIEW_COUNT;
    const visibleRunners = expanded ? ranked : ranked.slice(0, PREVIEW_COUNT);
    const visibleDnfs = expanded ? dnfs : (ranked.length >= PREVIEW_COUNT ? [] : dnfs.slice(0, PREVIEW_COUNT - ranked.length));
    const hiddenCount = allRunners.length - (expanded ? 0 : visibleRunners.length + visibleDnfs.length);

    return (
        <div>
            <span className={cn(
                "inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide mb-2",
                gender === 'MALE' ? "bg-blue-50 text-blue-600" : "bg-pink-50 text-pink-600"
            )}>
                {genderLabel}
            </span>

            {visibleRunners.length > 0 && (
                <div className="space-y-1">
                    {visibleRunners.map((ranking) => (
                        <div key={ranking.runner?.id} className="flex items-center gap-2 text-sm">
                            <span className="w-5 h-5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-black flex items-center justify-center flex-shrink-0">
                                {ranking.position}
                            </span>
                            <span className="text-slate-700 font-medium truncate">{ranking.runner?.name}</span>
                            <span className="text-[11px] text-slate-400 ml-auto flex-shrink-0">
                                UTMB {Math.round(ranking.runner?.utmbIndex || 0)}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {visibleDnfs.length > 0 && (
                <div className="space-y-1 mt-1">
                    {visibleDnfs.map((ranking) => (
                        <div key={ranking.runner?.id} className="flex items-center gap-2 text-sm">
                            <span className="w-5 h-5 rounded-md bg-slate-100 text-slate-500 text-[10px] font-black flex items-center justify-center flex-shrink-0">
                                &times;
                            </span>
                            <span className="text-slate-500 font-medium truncate">{ranking.runner?.name}</span>
                            <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">DNF</span>
                        </div>
                    ))}
                </div>
            )}

            {hasMore && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    aria-label={expanded ? t('list.showLess') : t('list.showMore', { count: hiddenCount })}
                    className="flex items-center gap-1 mt-2 text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors"
                >
                    {expanded ? (
                        <><ChevronUp size={14} />{t('list.showLess')}</>
                    ) : (
                        <><ChevronDown size={14} />{t('list.showMore', { count: hiddenCount })}</>
                    )}
                </button>
            )}
        </div>
    );
}
